import { Box, Typography } from "@mui/material";
import { t } from "i18next";
import { useState } from "react";
import { useCase } from "../../../context/CaseContext";
import {
  ensureApportionmentItems,
  getCoveredQuantums,
} from "../../../shared/apportionmentEngine";
import { getStages } from "../../../shared/British";
import { Stage } from "../../../shared/CaseConfiguration";
import {
  NamedEntity,
  OfferStatus,
  SettlementImpact,
} from "../../../shared/CaseModel";
import { SectionLabel } from "../../general/SectionLabel";
import { Marked } from "../../guide/GuideMarker";
import { YesNoDialog } from "../../general/YesNoDialog";
import { AcceptOfferDialog } from "./AcceptOfferDialog";
import { isDecided } from "./offerStatus";
import SettlementImpactInfoDialog from "./SettlementImpactInfoDialog";
import { Offer as CardOffer } from "./SettlementOfferCard";
import SettlementOfferDialog, { Offer } from "./SettlementOfferDialog";
import SettlementTimeline from "./SettlementTimeline";
import {
  isOpen,
  positionOf,
  chancesPerStage,
  settlesBeforeJudgment,
  SettlementChances,
} from "../../../shared/settlementProspects";
import { PercentSlider } from "./PercentSlider";
import { RangePanel } from "./RangePanel";
import {
  apportionedBy,
  isFullyApportioned,
  signedAmount,
} from "./settlementModel";

const SettlementOffers = ({
  party,
  opponent,
  offers,
  stages = getStages(),
  save,
  remove,
  create,
  readOnly = false,
  settled = false,
  current,
  judged = false,
  chances,
  onChanceChange,
  onPositionChange,
  sharedWith,
}: {
  party: NamedEntity;
  opponent: NamedEntity;
  offers: Offer[];
  stages: Stage[];
  save: (offer: Offer) => void;
  remove: (offerId: string) => void;
  create: () => Offer;
  // show the offers without the actions that would change them
  readOnly?: boolean;
  // the two parties have settled: the timeline is closed to further offers
  settled?: boolean;
  // the stage the case is at: the one open offers belong to
  current: Stage;
  // a judgment stands: nothing is left to settle
  judged?: boolean;
  chances?: SettlementChances;
  onChanceChange: (stage: Stage, chance: number) => void;
  onPositionChange: (position: number) => void;
  // who else owes a claimed amount the offer covers, and could settle it first
  sharedWith?: (offer: Offer) => string | undefined;
}) => {
  const [removedOffer, setRemovedOffer] = useState<Offer>();

  // A pending move OUT of a decided state, awaiting confirmation (it discards
  // the frozen impact snapshot).
  const [pendingStatusChange, setPendingStatusChange] = useState<{
    offerId: string;
    newStatus: OfferStatus;
    // the status being left (Rejected/Accepted) — drives the prompt copy
    fromStatus: OfferStatus;
  }>();
  // the offer being accepted, while the day of acceptance is asked for
  const [accepting, setAccepting] = useState<string>();
  const applyStatusChange = (
    offerId: string,
    newStatus: OfferStatus,
    acceptedOn?: string,
  ) => {
    const offer = offers.find(({ id }) => id === offerId);
    if (!offer) return;
    const offerCopy: Offer = JSON.parse(JSON.stringify(offer));
    offerCopy.status = newStatus;
    if (acceptedOn) offerCopy.acceptedOn = acceptedOn;
    else delete offerCopy.acceptedOn;
    save(offerCopy);
  };
  // an offer is accepted on a day the user enters
  const changeStatus = (offerId: string, newStatus: OfferStatus) =>
    newStatus === OfferStatus.Accepted
      ? setAccepting(offerId)
      : applyStatusChange(offerId, newStatus);

  const caseContext = useCase();

  const [editedOffer, setEditedOffer] = useState<Offer | undefined>();
  const [infoOffer, setInfoOffer] = useState<
    { offer: Offer; impact: SettlementImpact } | undefined
  >();

  const fallbackOf = (offer: Offer) => {
    const other = sharedWith?.(offer);
    if (!other || !isOpen(offer.status) || readOnly) return undefined;
    return (
      <>
        <Marked entry="ifFirst">
          <SectionLabel component="h3">
            {t("case.analytics.settlement.prospects.ifFirst", { name: other })}
          </SectionLabel>
        </Marked>
        <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
          {t("case.analytics.settlement.prospects.ifFirstNote", {
            party: party.name,
            opponent: opponent.name,
          })}
        </Typography>
        <PercentSlider
          value={positionOf(chances, party.id, opponent.id)}
          label={t("case.analytics.settlement.prospects.ifFirst", {
            name: other,
          })}
          onChange={onPositionChange}
        />
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            gap: 1,
            pr: 7,
            typography: "caption",
            color: "text.secondary",
          }}
        >
          <span>
            {t("case.analytics.settlement.prospects.worstFor", {
              share: "0%",
              name: party.name,
            })}
          </span>
          <span>
            {t("case.analytics.settlement.prospects.worstFor", {
              share: "100%",
              name: opponent.name,
            })}
          </span>
        </Box>
      </>
    );
  };

  const timelineOffers: CardOffer[] = offers.map((offer, index) => {
    const amounts = apportionedBy(offer);
    // What the party ends up with on average without the offer and with it,
    // from the same impact as the results dialog. The baseline is that of the
    // offer (live for a draft or a pending offer, frozen at the decision for a
    // decided one), unlike the baseline of the case, which after an acceptance
    // has the settlement in it. Left out where the stats have not been computed.
    const impact = caseContext.getOfferImpact(offer.id);
    const impactOnParty =
      offer.offeree.id === party.id ? impact?.offeree : impact?.offeror;
    const without = impactOnParty?.baselineStats?.netResult.average;
    const withOffer = impactOnParty?.settledStats?.netResult.average;

    return {
      id: offer.id,
      fallback: fallbackOf(offer),
      number: index + 1,
      offeror: offer.offeror,
      offeree: offer.offeree,
      byParty: offer.offeror.id === party.id,
      amount: offer.payment.amount,
      payer: offer.payment.offereePays ? offer.offeree : offer.offeror,
      stage: offer.stage,
      status: offer.status,
      ...(offer.acceptedOn && { acceptedOn: offer.acceptedOn }),
      claims: offer.claims
        .filter((claim) => claim.settledByOffer)
        .map(({ number, title }) => ({ number, title })),
      amounts: getCoveredQuantums(offer.claims, offer.quantums).map(
        ({ id, title, value }) => ({
          title,
          value,
          paid: amounts.quantums[id] ?? 0,
        }),
      ),
      fullyApportioned: isFullyApportioned(amounts),
      netResult:
        without !== undefined && withOffer !== undefined
          ? { partyName: party.name, without, with: withOffer }
          : undefined,
    };
  });

  // the settlement that closes the timeline: the last offer that was accepted
  const accepted = offers.filter(
    ({ status }) => status === OfferStatus.Accepted,
  );
  const closing = settled ? accepted[accepted.length - 1] : undefined;

  // Signed bargaining midpoint per stage from the offeror's perspective
  // (positive ⇒ offeror is the net receiver, i.e. offeree pays). The dialog
  // turns this into a directional "who pays whom + amount" suggestion.
  const settlementData = caseContext.getSettlementData();
  const midpointsByStageFor = (
    offerorId: string,
    offereeId: string,
  ): { [stage: number]: number } => {
    const ranges = settlementData
      .find(({ partyId }) => partyId === offerorId)
      ?.data.find(({ opponentId }) => opponentId === offereeId);
    const map: { [stage: number]: number } = {};
    ranges?.perStage.forEach(({ stage, range }) => {
      map[stage] = range.mid;
    });
    return map;
  };

  const rangeOf = (stage: Stage) =>
    settlementData
      .find(({ partyId }) => partyId === party.id)
      ?.data.find(({ opponentId }) => opponentId === opponent.id)
      ?.perStage.find((ofStage) => ofStage.stage === stage)?.range;

  const inForce = !settled && !judged;
  const onTheTable = offers.filter(({ status }) => isOpen(status)).pop();
  const position = positionOf(chances, party.id, opponent.id);
  // what the chances come to is only arithmetic of the chances; what they are
  // worth comes from the server (see AdjustedValue)
  const rows = chancesPerStage({
    stages: inForce
      ? stages.filter((s) => s >= current && settlesBeforeJudgment(s))
      : [],
    chances,
    position,
    rangeOf,
    onTheTable: onTheTable && signedAmount(onTheTable, party.id),
  });
  const litigation = rows[rows.length - 1]?.after ?? 1;

  // the amount of the slider makes a draft; without one, the dialog asks for it
  const createOffer = (amount: number | undefined) => {
    const offer = create();
    if (amount === undefined) {
      setEditedOffer(offer);
      return;
    }
    // the range prices all that is left between the two, so that is what it settles
    const settled = offers
      .filter(({ status }) => status === OfferStatus.Accepted)
      .flatMap(({ claims }) => claims)
      .filter(({ settledByOffer }) => settledByOffer)
      .map(({ id }) => id);
    const claims = offer.claims.map((claim) => ({
      ...claim,
      settledByOffer: !settled.includes(claim.id),
    }));
    save({
      ...offer,
      claims,
      apportionment: ensureApportionmentItems(
        offer.apportionment,
        getCoveredQuantums(claims, offer.quantums).map(({ id }) => id),
      ),
      payment: { amount: Math.round(Math.abs(amount)), offereePays: amount > 0 },
    });
  };

  return (
    <>
      <SettlementTimeline
        partyName={party.name}
        opponentName={opponent.name}
        offers={timelineOffers}
        settlement={
          closing
            ? { stage: closing.stage, amount: closing.payment.amount }
            : undefined
        }
        stages={stages
          .filter((stage) => !closing || stage <= closing.stage)
          .map((stage) => ({
            stage,
            status:
              stage < current
                ? "closed"
                : stage === current
                  ? "current"
                  : "coming",
            chance: chances?.chances?.[stage] ?? 0,
            prospect: rows.find((row) => row.stage === stage),
          }))}
        rangePanel={
          inForce &&
          !onTheTable && (
            <RangePanel
              partyName={party.name}
              opponentName={opponent.name}
              range={rangeOf(current)}
              position={position}
              readOnly={readOnly}
              onChange={onPositionChange}
              onCreate={createOffer}
            />
          )
        }
        resulting={
          inForce ? { settling: 1 - litigation, litigation } : undefined
        }
        // a timeline without a callback leaves the matching action out
        onChanceChange={readOnly ? undefined : onChanceChange}
        onDeleteRequested={
          readOnly
            ? undefined
            : (offerId) =>
                setRemovedOffer(offers.find(({ id }) => id === offerId))
        }
        onEditRequested={
          readOnly
            ? undefined
            : (offerId) => {
                // ignore if an offer is already being edited
                if (editedOffer) return;
                setEditedOffer(offers.find(({ id }) => id === offerId));
              }
        }
        onInfoRequested={
          readOnly
            ? undefined
            : (offerId) => {
                // ignore if the results of an offer are already shown
                if (infoOffer) return;

                const offer = offers.find(({ id }) => id === offerId);
                if (!offer) return;

                const impact = caseContext.getOfferImpact(offerId);
                if (!impact) return;
                setInfoOffer({ offer, impact });
              }
        }
        onStatusChange={
          readOnly
            ? undefined
            : (offerId, newStatus) => {
                const offer = offers.find(({ id }) => id === offerId);
                if (!offer) return;
                // Leaving a decided state (Rejected/Accepted) discards the impact
                // snapshot frozen at the decision and recalculates from the current
                // case — confirm before doing so.
                if (isDecided(offer.status) && !isDecided(newStatus)) {
                  setPendingStatusChange({
                    offerId,
                    newStatus,
                    fromStatus: offer.status,
                  });
                  return;
                }
                changeStatus(offerId, newStatus);
              }
        }
      />
      {accepting && (
        <AcceptOfferDialog
          onClose={(acceptedOn) => {
            if (acceptedOn)
              applyStatusChange(accepting, OfferStatus.Accepted, acceptedOn);
            setAccepting(undefined);
          }}
        />
      )}
      {editedOffer && (
        <SettlementOfferDialog
          initialOffer={editedOffer}
          open
          midpointByStage={midpointsByStageFor(
            editedOffer.offeror.id,
            editedOffer.offeree.id,
          )}
          onClose={(editedOffer) => {
            setEditedOffer(undefined);
            if (!editedOffer)
              // edit cancelled
              return;
            save(editedOffer);
          }}
        />
      )}
      {infoOffer && (
        <SettlementImpactInfoDialog
          title={t("case.analytics.settlement.offerDialog.title", {
            offeror: infoOffer.offer.offeror.name,
          })}
          offer={infoOffer.offer}
          impact={infoOffer.impact}
          partyId={party.id}
          open
          onClose={() => setInfoOffer(undefined)}
        />
      )}
      <YesNoDialog
        open={removedOffer !== undefined}
        title={t("case.analytics.settlement.deleteOfferDialog.title")}
        description={t("case.analytics.settlement.deleteOfferDialog.question", {
          offeror: removedOffer?.offeror.name,
        })}
        yesColor="error"
        onYes={() => {
          if (removedOffer) remove(removedOffer.id);
          setRemovedOffer(undefined);
        }}
        onNo={() => setRemovedOffer(undefined)}
      />
      <YesNoDialog
        open={pendingStatusChange !== undefined}
        title={t("case.analytics.settlement.reopenOfferDialog.title")}
        description={t("case.analytics.settlement.reopenOfferDialog.question", {
          status: t(
            `case.analytics.settlement.reopenOfferDialog.status.${
              pendingStatusChange?.fromStatus === OfferStatus.Accepted
                ? "accepted"
                : "rejected"
            }`,
          ),
        })}
        yesColor="warning"
        onYes={() => {
          if (pendingStatusChange)
            changeStatus(
              pendingStatusChange.offerId,
              pendingStatusChange.newStatus,
            );
          setPendingStatusChange(undefined);
        }}
        onNo={() => setPendingStatusChange(undefined)}
      />
    </>
  );
};

export default SettlementOffers;
